import { Link, useNavigate } from 'react-router-dom';
import { Chip, DropdownMenuItem, Tooltip } from '@c2fo/liquidity';
import { EyeIcon, PencilIcon } from '@c2fo/liquidity/icons';
import { CopyableId } from '../CopyableId';
import { useSchedulesState } from '../../contexts';
import type { SchedulesSortField } from '../../contexts/FileDeliveryContext';
import { TableHeader } from '../TableHeader';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell } from '../DataTable';
import { ActionsMenu } from '../ActionsMenu';
import { UriLink } from '../UriLink';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

export const ScheduleTable: React.FC = () => {
  const navigate = useNavigate();
  const { schedules, sortField, sortDirection, setSort } = useSchedulesState();

  const handleSort = (field: SchedulesSortField) => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSort(field, newDirection);
    } else {
      setSort(field, 'asc');
    }
  };

  return (
    <DataTable>
      <DataTableHead>
        <TableHeader label="ID" className="w-24" />
        <TableHeader label="Source → Target" />
        <TableHeader label="File Pattern" />
        <TableHeader label="Type" align="center" className="w-20" />
        <TableHeader label="Schedule" />
        <TableHeader
          field="create_time"
          label="Created"
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          onSort={handleSort}
          className="w-40"
        />
        <TableHeader
          field="update_time"
          label="Updated"
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          onSort={handleSort}
          className="w-40"
        />
        <TableHeader label="" className="w-16" />
      </DataTableHead>
      <DataTableBody>
        {schedules.map((schedule) => (
          <DataTableRow key={schedule.uid}>
            <DataTableCell variant="primary" className="max-w-24">
              <CopyableId value={schedule.uid} />
            </DataTableCell>
            <DataTableCell variant="secondary" className="whitespace-nowrap">
              <div className="flex items-center gap-2">
                <Tooltip.Provider>
                  <Tooltip>
                    <Tooltip.Trigger asChild>
                      <Link
                        to="/file-delivery/locations"
                        className="text-gray-700 font-medium text-xs hover:text-primary-600 hover:underline"
                      >
                        {schedule.sourceLocation?.displayName ?? 'Unknown'}
                      </Link>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content variant="dark" arrow>
                        {schedule.sourceLocation?.uri ? (
                          <UriLink
                            uri={schedule.sourceLocation.uri}
                            truncate={false}
                            showTooltip={false}
                            className="text-white"
                          />
                        ) : (
                          'No URI'
                        )}
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip>
                </Tooltip.Provider>
                <span className="text-gray-400">→</span>
                <Tooltip.Provider>
                  <Tooltip>
                    <Tooltip.Trigger asChild>
                      <Link
                        to="/file-delivery/locations"
                        className="text-gray-700 font-medium text-xs hover:text-primary-600 hover:underline"
                      >
                        {schedule.targetLocation?.displayName ?? 'Unknown'}
                      </Link>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content variant="dark" arrow>
                        {schedule.targetLocation?.uri ? (
                          <UriLink
                            uri={schedule.targetLocation.uri}
                            truncate={false}
                            showTooltip={false}
                            className="text-white"
                          />
                        ) : (
                          'No URI'
                        )}
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip>
                </Tooltip.Provider>
              </div>
            </DataTableCell>
            <DataTableCell variant="secondary">
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">{schedule.sourcePath}</code>
            </DataTableCell>
            <DataTableCell align="center">
              <Chip
                label={schedule.scheduleTypeLabel}
                type={schedule.scheduleTypeLabel === 'Cron' ? 'primary' : 'secondary'}
                variant="outlined"
                size="xs"
              />
            </DataTableCell>
            <DataTableCell variant="secondary">
              <Tooltip.Provider>
                <Tooltip>
                  <Tooltip.Trigger asChild>
                    <span className="cursor-help">{schedule.scheduleDescription}</span>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content variant="dark" arrow>
                      <div className="flex flex-col gap-1">
                        <span>Expression: {schedule.schedule.expression}</span>
                        {schedule.schedule.timezone && <span>Timezone: {schedule.schedule.timezone}</span>}
                      </div>
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip>
              </Tooltip.Provider>
            </DataTableCell>
            <DataTableCell variant="date">
              {schedule.createTime ? formatDateWithTimezone(schedule.createTime) : '-'}
            </DataTableCell>
            <DataTableCell variant="date">
              {schedule.updateTime ? formatDateWithTimezone(schedule.updateTime) : '-'}
            </DataTableCell>
            <DataTableCell align="center">
              <ActionsMenu>
                <DropdownMenuItem
                  className="active:bg-lightBlue-100"
                  onSelect={() => navigate(`/file-delivery/scheduledDeliveries/${schedule.uid}`)}
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="active:bg-lightBlue-100"
                  onSelect={() => navigate(`/file-delivery/scheduledDeliveries/${schedule.uid}/edit`)}
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              </ActionsMenu>
            </DataTableCell>
          </DataTableRow>
        ))}
      </DataTableBody>
    </DataTable>
  );
};
